import React, { useState, useMemo } from 'react';
import usePlants from '../hooks/usePlants';
import WetlandRatingSelect from './WetlandRatingSelect';
import './AddPlantForm.css';

function AddPlantForm({ gridRow, gridCol, onSubmit, onCancel }) {
  const { species, searchSpecies, addSpecies } = usePlants();

  const [query, setQuery] = useState('');
  const [selectedSpecies, setSelectedSpecies] = useState(null);
  const [showNewSpeciesForm, setShowNewSpeciesForm] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Instance fields
  const [source, setSource] = useState('');
  const [trayNumber, setTrayNumber] = useState('');
  const [qtySoloCups, setQtySoloCups] = useState(0);
  const [qtyPlugs, setQtyPlugs] = useState(0);
  const [qtyQuarts, setQtyQuarts] = useState(0);
  const [qtyGallons, setQtyGallons] = useState(0);

  // New species fields
  const [newCommonName, setNewCommonName] = useState('');
  const [newScientificName, setNewScientificName] = useState('');
  const [newAgcpRating, setNewAgcpRating] = useState(null);
  const [newEmpRating, setNewEmpRating] = useState(null);
  const [newLight, setNewLight] = useState('');
  const [newMoisture, setNewMoisture] = useState('');
  const [newSoil, setNewSoil] = useState('');
  const [newBloomSeason, setNewBloomSeason] = useState('');
  const [newBloomColor, setNewBloomColor] = useState('');
  const [newSaltTolerance, setNewSaltTolerance] = useState(null);

  const suggestions = useMemo(() => {
    if (!query.trim()) return [];
    return searchSpecies(query).slice(0, 8);
  }, [query, searchSpecies]);

  const handleSelectSpecies = (sp) => {
    setSelectedSpecies(sp);
    setQuery(sp.commonName);
    setShowSuggestions(false);
    setShowNewSpeciesForm(false);
  };

  const handleCreateNew = () => {
    setShowNewSpeciesForm(true);
    setShowSuggestions(false);
    setSelectedSpecies(null);
    setNewCommonName(query);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let speciesRecord = selectedSpecies;

    // If creating a new species, save it first
    if (showNewSpeciesForm) {
      if (!newCommonName.trim() || !newScientificName.trim()) {
        return; // basic validation
      }
      speciesRecord = await addSpecies({
        commonName: newCommonName.trim(),
        scientificName: newScientificName.trim(),
        agcpRating: newAgcpRating,
        empRating: newEmpRating,
        lightRequirements: newLight,
        moistureRequirements: newMoisture,
        soilRequirements: newSoil,
        bloomSeason: newBloomSeason,
        bloomColor: newBloomColor,
        saltTolerance: newSaltTolerance,
      });
    }

    if (!speciesRecord) return;

    // Build the plant instance data with denormalized species fields
    const plantData = {
      speciesId: speciesRecord.id,
      gridRow,
      gridCol,
      commonName: speciesRecord.commonName,
      scientificName: speciesRecord.scientificName,
      agcpRating: speciesRecord.agcpRating,
      empRating: speciesRecord.empRating,
      lightRequirements: speciesRecord.lightRequirements || '',
      moistureRequirements: speciesRecord.moistureRequirements || '',
      soilRequirements: speciesRecord.soilRequirements || '',
      bloomSeason: speciesRecord.bloomSeason || '',
      bloomColor: speciesRecord.bloomColor || '',
      saltTolerance: speciesRecord.saltTolerance || null,
      source,
      trayNumber,
      qtyPlugs,
      qtySoloCups,
      qtyQuarts,
      qtyGallons,
    };

    onSubmit(plantData);
  };

  return (
    <form className="add-plant-form" onSubmit={handleSubmit} noValidate>
      <h2 className="add-plant-form__title">Add Plant to Cell ({gridRow}, {gridCol})</h2>

      {/* Species autocomplete */}
      <div className="add-plant-form__section">
        <h3>Species</h3>
        {selectedSpecies ? (
          <div className="add-plant-form__selected-species">
            <p><strong>{selectedSpecies.commonName}</strong> — <em>{selectedSpecies.scientificName}</em></p>
            <button type="button" className="add-plant-form__change-btn" onClick={() => { setSelectedSpecies(null); setQuery(''); }}>
              Change
            </button>
          </div>
        ) : !showNewSpeciesForm ? (
          <div className="add-plant-form__autocomplete">
            <label htmlFor="species-search">Search species</label>
            <input
              id="species-search"
              type="text"
              className="add-plant-form__input"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setShowSuggestions(true); }}
              onFocus={() => setShowSuggestions(true)}
              placeholder="Type common or scientific name..."
              autoComplete="off"
            />
            {showSuggestions && query.trim() && (
              <div className="add-plant-form__suggestions">
                {suggestions.length > 0 ? (
                  <ul>
                    {suggestions.map((sp) => (
                      <li key={sp.id}>
                        <button type="button" onClick={() => handleSelectSpecies(sp)}>
                          <strong>{sp.commonName}</strong> — <em>{sp.scientificName}</em>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="add-plant-form__no-match">No species found</p>
                )}
                <button type="button" className="add-plant-form__new-btn" onClick={handleCreateNew}>
                  + Add new species "{query}"
                </button>
              </div>
            )}
          </div>
        ) : null}
      </div>

      {/* New species form */}
      {showNewSpeciesForm && (
        <div className="add-plant-form__section add-plant-form__new-species">
          <h3>New Species</h3>
          <div className="add-plant-form__field">
            <label htmlFor="new-common">Common Name *</label>
            <input id="new-common" type="text" className="add-plant-form__input" value={newCommonName} onChange={(e) => setNewCommonName(e.target.value)} required />
          </div>
          <div className="add-plant-form__field">
            <label htmlFor="new-scientific">Scientific Name *</label>
            <input id="new-scientific" type="text" className="add-plant-form__input" value={newScientificName} onChange={(e) => setNewScientificName(e.target.value)} required />
          </div>
          <WetlandRatingSelect id="new-agcp" label="AGCP Rating" value={newAgcpRating} onChange={setNewAgcpRating} />
          <WetlandRatingSelect id="new-emp" label="EMP Rating" value={newEmpRating} onChange={setNewEmpRating} />
          <div className="add-plant-form__field">
            <label htmlFor="new-light">Light Requirements</label>
            <input id="new-light" type="text" className="add-plant-form__input" value={newLight} onChange={(e) => setNewLight(e.target.value)} />
          </div>
          <div className="add-plant-form__field">
            <label htmlFor="new-moisture">Moisture Requirements</label>
            <input id="new-moisture" type="text" className="add-plant-form__input" value={newMoisture} onChange={(e) => setNewMoisture(e.target.value)} />
          </div>
          <div className="add-plant-form__field">
            <label htmlFor="new-soil">Soil Requirements</label>
            <input id="new-soil" type="text" className="add-plant-form__input" value={newSoil} onChange={(e) => setNewSoil(e.target.value)} />
          </div>
          <div className="add-plant-form__field">
            <label htmlFor="new-bloom-season">Bloom Season</label>
            <input id="new-bloom-season" type="text" className="add-plant-form__input" value={newBloomSeason} onChange={(e) => setNewBloomSeason(e.target.value)} />
          </div>
          <div className="add-plant-form__field">
            <label htmlFor="new-bloom-color">Bloom Color</label>
            <input id="new-bloom-color" type="text" className="add-plant-form__input" value={newBloomColor} onChange={(e) => setNewBloomColor(e.target.value)} />
          </div>
          <div className="add-plant-form__field">
            <label htmlFor="new-salt-tolerance">Salt Tolerance (1-5) {newSaltTolerance ? `: ${newSaltTolerance}` : ''}</label>
            <input id="new-salt-tolerance" type="range" min="1" max="5" step="1" value={newSaltTolerance || 3} onChange={(e) => setNewSaltTolerance(parseInt(e.target.value, 10))} className="add-plant-form__slider" />
          </div>
          <button type="button" className="add-plant-form__back-btn" onClick={() => { setShowNewSpeciesForm(false); setQuery(''); }}>
            ← Back to search
          </button>
        </div>
      )}

      {/* Instance fields (always shown) */}
      <div className="add-plant-form__section">
        <h3>Instance Details</h3>
        <div className="add-plant-form__field">
          <label htmlFor="inst-source">Source</label>
          <input id="inst-source" type="text" className="add-plant-form__input" value={source} onChange={(e) => setSource(e.target.value)} />
        </div>
        <div className="add-plant-form__field">
          <label htmlFor="inst-tray">Tray Number</label>
          <input id="inst-tray" type="text" className="add-plant-form__input" value={trayNumber} onChange={(e) => setTrayNumber(e.target.value)} placeholder="e.g. T1, Mixed-A" />
        </div>
        <div className="add-plant-form__quantities">
          <div className="add-plant-form__field">
            <label htmlFor="inst-plugs">Plugs</label>
            <input id="inst-plugs" type="number" min="0" className="add-plant-form__input" value={qtyPlugs} onChange={(e) => setQtyPlugs(parseInt(e.target.value, 10) || 0)} />
          </div>
          <div className="add-plant-form__field">
            <label htmlFor="inst-solo">Solo Cups</label>
            <input id="inst-solo" type="number" min="0" className="add-plant-form__input" value={qtySoloCups} onChange={(e) => setQtySoloCups(parseInt(e.target.value, 10) || 0)} />
          </div>
          <div className="add-plant-form__field">
            <label htmlFor="inst-quarts">Quarts</label>
            <input id="inst-quarts" type="number" min="0" className="add-plant-form__input" value={qtyQuarts} onChange={(e) => setQtyQuarts(parseInt(e.target.value, 10) || 0)} />
          </div>
          <div className="add-plant-form__field">
            <label htmlFor="inst-gallons">Gallons</label>
            <input id="inst-gallons" type="number" min="0" className="add-plant-form__input" value={qtyGallons} onChange={(e) => setQtyGallons(parseInt(e.target.value, 10) || 0)} />
          </div>
        </div>
      </div>

      <div className="add-plant-form__actions">
        <button type="submit" className="add-plant-form__submit-btn" disabled={!selectedSpecies && !showNewSpeciesForm}>
          Add Plant
        </button>
        <button type="button" className="add-plant-form__cancel-btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

export default AddPlantForm;
